import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AutoAwesomeMosaicIcon from "@mui/icons-material/AutoAwesomeMosaic";
import BookmarkAddedRoundedIcon from "@mui/icons-material/BookmarkAddedRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonSearchOutlinedIcon from "@mui/icons-material/PersonSearchOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import StepConnector, {
  stepConnectorClasses,
} from "@mui/material/StepConnector";
import { StepIconProps } from "@mui/material/StepIcon";
import { styled } from "@mui/material/styles";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import backArrow from "../../../assets/icons/backArrow.svg";
import { CustomButton } from "../../../global/components";
import CustomLoader from "../../../global/components/CustomLoader/CustomLoader";
import strings from "../../../global/constants/StringConstants";
import {
  handleErrorMessage,
  openSuccessNotification,
} from "../../../helpers/methods";
import history from "../../../utils/history";
import { theme } from "../../../utils/styles";
import { getProfile } from "../../Dashboard/DashboardService";
import professionPhasedStyles from "./ProfessionPhased.styles";
import {
  downloadPhasedAnalysis,
  generateProfessionPhasedAnalysis,
  getSavedPrompts,
  savePhasePrompt,
} from "./ProfessionPhasedService";

const TOTAL_PHASES = 4;

// ─── Stepper connector with gradient progress ──────────────────────────────
const GradientConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 21,
    left: "calc(-50% + 28px)",
    right: "calc(50% + 28px)",
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundImage: "linear-gradient(90deg, #9053F6, #5E75FF)",
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundImage: "linear-gradient(90deg, #2CF1AA, #9053F6)",
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 3,
    border: 0,
    backgroundColor: "rgba(255,255,255,0.1)",
    borderRadius: 2,
  },
}));

// ─── Custom step icon ──────────────────────────────────────────────────────
function CustomStepIcon(props: StepIconProps) {
  const { active, completed, icon } = props;
  return (
    <Box
      sx={{
        width: 44,
        height: 44,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: completed
          ? "linear-gradient(135deg, #2CF1AA, #00D4AA)"
          : active
            ? "linear-gradient(135deg, #9053F6, #5E75FF 60%, #2CF1DF)"
            : "rgba(255,255,255,0.07)",
        border: completed
          ? "2px solid rgba(44,241,170,0.5)"
          : active
            ? "2px solid rgba(144,83,246,0.6)"
            : "2px solid rgba(255,255,255,0.1)",
        boxShadow: active
          ? "0 0 20px rgba(144,83,246,0.55)"
          : completed
            ? "0 0 14px rgba(44,241,170,0.4)"
            : "none",
        transition: "all 0.35s ease",
        color: active || completed ? "#fff" : "rgba(255,255,255,0.3)",
        fontWeight: 700,
        fontSize: "14px",
      }}
    >
      {completed ? <CheckRoundedIcon sx={{ fontSize: 20 }} /> : icon}
    </Box>
  );
}

// ─── Markdown renderer shared config ─────────────────────────────────────
const markdownComponents: any = {
  p: (p: any) => (
    <p
      style={{
        margin: "0 0 10px 0",
        color: "rgba(255,255,255,0.85)",
        lineHeight: 1.75,
      }}
      {...p}
    />
  ),
  h1: (p: any) => (
    <h1
      style={{
        margin: "18px 0 8px",
        color: "#fff",
        fontSize: "20px",
        fontWeight: 700,
        borderBottom: "1px solid rgba(144,83,246,0.3)",
        paddingBottom: 6,
      }}
      {...p}
    />
  ),
  h2: (p: any) => (
    <h2
      style={{
        margin: "14px 0 6px",
        color: "#fff",
        fontSize: "17px",
        fontWeight: 600,
      }}
      {...p}
    />
  ),
  h3: (p: any) => (
    <h3
      style={{
        margin: "12px 0 4px",
        color: "rgba(255,255,255,0.9)",
        fontSize: "15px",
        fontWeight: 600,
      }}
      {...p}
    />
  ),
  h4: (p: any) => (
    <h4
      style={{ margin: "10px 0 4px", color: "rgba(255,255,255,0.85)" }}
      {...p}
    />
  ),
  h5: (p: any) => (
    <h5
      style={{ margin: "8px 0 2px", color: "rgba(255,255,255,0.8)" }}
      {...p}
    />
  ),
  h6: (p: any) => (
    <h6
      style={{ margin: "6px 0 2px", color: "rgba(255,255,255,0.7)" }}
      {...p}
    />
  ),
  ul: (p: any) => (
    <ul
      style={{
        margin: "4px 0 10px",
        paddingLeft: 22,
        color: "rgba(255,255,255,0.82)",
        lineHeight: 1.7,
      }}
      {...p}
    />
  ),
  ol: (p: any) => (
    <ol
      style={{
        margin: "4px 0 10px",
        paddingLeft: 24,
        color: "rgba(255,255,255,0.82)",
        lineHeight: 1.7,
      }}
      {...p}
    />
  ),
  li: (p: any) => <li style={{ marginBottom: 4 }} {...p} />,
  strong: (p: any) => (
    <strong style={{ color: "#C39BFF", fontWeight: 600 }} {...p} />
  ),
  em: (p: any) => (
    <em style={{ color: "rgba(44,241,223,0.9)", fontStyle: "italic" }} {...p} />
  ),
  blockquote: (p: any) => (
    <blockquote
      style={{
        margin: "10px 0",
        paddingLeft: 14,
        borderLeft: "3px solid rgba(144,83,246,0.6)",
        color: "rgba(255,255,255,0.6)",
        fontStyle: "italic",
      }}
      {...p}
    />
  ),
  code: (p: any) => (
    <code
      style={{
        background: "rgba(144,83,246,0.15)",
        borderRadius: 4,
        padding: "2px 6px",
        fontSize: 13,
        color: "#C39BFF",
      }}
      {...p}
    />
  ),
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: "1px solid rgba(255,255,255,0.1)",
        margin: "12px 0",
      }}
    />
  ),
  a: (p: any) => (
    <a
      style={{ color: "#8A31FF", textDecoration: "underline" }}
      target="_blank"
      rel="noopener noreferrer"
      {...p}
    />
  ),
  table: (p: any) => (
    <table
      style={{
        borderCollapse: "collapse",
        width: "100%",
        margin: "10px 0",
        fontSize: 13,
      }}
      {...p}
    />
  ),
  th: (p: any) => (
    <th
      style={{
        background: "rgba(144,83,246,0.3)",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.15)",
        padding: "6px 10px",
        textAlign: "left",
      }}
      {...p}
    />
  ),
  td: (p: any) => (
    <td
      style={{
        color: "rgba(255,255,255,0.8)",
        border: "1px solid rgba(255,255,255,0.1)",
        padding: "5px 10px",
      }}
      {...p}
    />
  ),
};

// ─── Phase config ─────────────────────────────────────────────────────────
const PHASE_AGENT_NAMES = [
  "phaseAgentName1",
  "phaseAgentName2",
  "phaseAgentName3",
  "phaseAgentName4",
];

const PHASE_SUBTITLES = [
  "phaseSubtitle1",
  "phaseSubtitle2",
  "phaseSubtitle3",
  "phaseSubtitle4",
];

// ─── Main component ───────────────────────────────────────────────────────
const ProfessionPhased = () => {
  const cls = professionPhasedStyles;
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [profiles, setProfiles] = useState<any[]>([]);
  const [profile, setProfile] = useState<string>("");
  const [isProfilesLoading, setIsProfilesLoading] = useState(false);

  const [prompts, setPrompts] = useState<string[]>(
    Array(TOTAL_PHASES).fill(""),
  );
  const [outputs, setOutputs] = useState<string[]>(
    Array(TOTAL_PHASES).fill(""),
  );
  const [loadings, setLoadings] = useState<boolean[]>(
    Array(TOTAL_PHASES).fill(false),
  );
  const [currentPhase, setCurrentPhase] = useState<number>(1);

  // Which completed-phase accordions are manually expanded by the user
  const [expandedCompleted, setExpandedCompleted] = useState<Set<number>>(
    new Set(),
  );

  // Save prompt state: index being saved, set of recently saved indices
  const [savingPhase, setSavingPhase] = useState<number | null>(null);
  const [savedPhases, setSavedPhases] = useState<Set<number>>(new Set());
  const [isFetchingPrompts, setIsFetchingPrompts] = useState(false);

  // Saved prompt templates fetched once on mount (global, not per-profile)
  const [savedPromptTexts, setSavedPromptTexts] = useState<string[]>(
    Array(TOTAL_PHASES).fill(""),
  );

  // DOCX download state
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    fetchProfiles();
    [1, 2, 3, 4].forEach((phase) => loadSavedPrompts(phase));
  }, []);

  const fetchProfiles = async () => {
    try {
      setIsProfilesLoading(true);
      const response = await getProfile();
      setProfiles(response);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsProfilesLoading(false);
    }
  };

  const handleGenerate = async (phaseIndex: number) => {
    if (!profile || !prompts[phaseIndex].trim()) return;
    const phase = phaseIndex + 1;
    const previousMarkdown =
      phaseIndex > 0 ? outputs[phaseIndex - 1] : undefined;

    try {
      setLoadings((prev) => {
        const n = [...prev];
        n[phaseIndex] = true;
        return n;
      });
      const response = await generateProfessionPhasedAnalysis(
        profile,
        i18n.language.toLowerCase(),
        prompts[phaseIndex],
        phase,
        previousMarkdown,
      );
      const res: any = response;
      const markdown =
        res?.markdown ||
        res?.content ||
        res?.text ||
        (typeof res === "string" ? res : JSON.stringify(res, null, 2));

      setOutputs((prev) => {
        const n = [...prev];
        n[phaseIndex] = markdown;
        return n;
      });
      openSuccessNotification(t("phaseCompletedMsg", { phase }));
      // Show only the just-completed phase; collapse all previous ones
      setExpandedCompleted(new Set([phaseIndex]));
      setCurrentPhase(phase < TOTAL_PHASES ? phase + 1 : TOTAL_PHASES + 1);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setLoadings((prev) => {
        const n = [...prev];
        n[phaseIndex] = false;
        return n;
      });
    }
  };

  // Fetch saved prompt for a specific phase (called once per phase on mount)
  const loadSavedPrompts = async (phase: number) => {
    try {
      setIsFetchingPrompts(true);
      const response = await getSavedPrompts(phase);
      const phaseNum: number = response?.phase ?? phase;
      const savedPrompt: string = response?.prompt || "";
      if (savedPrompt && phaseNum >= 1 && phaseNum <= TOTAL_PHASES) {
        setSavedPromptTexts((prev) => {
          const next = [...prev];
          next[phaseNum - 1] = savedPrompt;
          return next;
        });
      }
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsFetchingPrompts(false);
    }
  };

  const handleSavePrompt = async (phaseIndex: number) => {
    if (!prompts[phaseIndex].trim()) return;
    try {
      setSavingPhase(phaseIndex);
      await savePhasePrompt(phaseIndex + 1, prompts[phaseIndex]);
      // Update the local saved template too so the card stays current
      setSavedPromptTexts((prev) => {
        const next = [...prev];
        next[phaseIndex] = prompts[phaseIndex];
        return next;
      });
      setSavedPhases((prev) => new Set(Array.from(prev).concat(phaseIndex)));
      setTimeout(() => {
        setSavedPhases((prev) => {
          const next = new Set(prev);
          next.delete(phaseIndex);
          return next;
        });
      }, 2500);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setSavingPhase(null);
    }
  };

  const handleApplySavedPrompt = (phaseIndex: number) => {
    const saved = savedPromptTexts[phaseIndex];
    if (!saved) return;
    setPrompts((prev) => {
      const next = [...prev];
      next[phaseIndex] = saved;
      return next;
    });
  };

  const handleDownloadDocx = async () => {
    const lastOutput = outputs[TOTAL_PHASES - 1];
    if (!lastOutput) return;
    const profileName =
      profiles.find((p) => p.profileId === profile)?.name || "analysis";
    try {
      setIsDownloading(true);
      const blob = await downloadPhasedAnalysis(lastOutput);
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${profileName}.docx`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleProfileChange = (_: any, value: any) => {
    setProfile(value?.profileId || "");
    setPrompts(Array(TOTAL_PHASES).fill(""));
    setOutputs(Array(TOTAL_PHASES).fill(""));
    setLoadings(Array(TOTAL_PHASES).fill(false));
    setCurrentPhase(1);
    setExpandedCompleted(new Set());
    setSavedPhases(new Set());
    // savedPromptTexts intentionally NOT reset — they're global templates
  };

  // Reset phaseIndex and all subsequent phases so user can regenerate
  const handleRegenerate = (phaseIndex: number) => {
    setOutputs((prev) => {
      const n = [...prev];
      for (let k = phaseIndex; k < TOTAL_PHASES; k++) n[k] = "";
      return n;
    });
    setExpandedCompleted((prev) => {
      const next = new Set(prev);
      for (let k = phaseIndex; k < TOTAL_PHASES; k++) next.delete(k);
      return next;
    });
    setCurrentPhase(phaseIndex + 1);
  };

  const handleCompletedToggle = (i: number) => {
    setExpandedCompleted((prev) => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const isPhaseActive = (i: number) => currentPhase === i + 1;
  const isPhaseCompleted = (i: number) => currentPhase > i + 1;
  const isAnyLoading = loadings.some(Boolean) || isFetchingPrompts;
  const allDone = currentPhase > TOTAL_PHASES;

  const getAccordionExpanded = (i: number): boolean => {
    if (isPhaseActive(i) && !!profile) return true;
    if (isPhaseCompleted(i)) return expandedCompleted.has(i);
    return false;
  };

  // ─── Header ──────────────────────────────────────────────────────────────
  const renderHeader = () => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: { sm: "center", xs: "baseline" },
        width: "100%",
      }}
      pb={2}
    >
      <Box
        display="flex"
        alignItems="center"
        sx={{ cursor: "pointer", width: "fit-content" }}
        onClick={() => history.goBack()}
      >
        <img src={backArrow} height={isMobile ? "20px" : "25px"} alt="back" />
      </Box>
      <Typography sx={cls.pageTitle}>{t("professionPhasedTitle")}</Typography>
    </Box>
  );

  // ─── Profile selector card ────────────────────────────────────────────────
  const renderProfileCard = () => (
    <Box sx={cls.profileCard}>
      <Box sx={cls.profileIconWrap}>
        <PersonSearchOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
      </Box>
      <Box sx={cls.profileSelectorGroup}>
        <Typography sx={cls.profileLabel}>{t("selectProfile")}</Typography>
        <Autocomplete
          sx={cls.autoComplete}
          options={profiles || []}
          getOptionLabel={(option: any) => option.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={t("searchProfile")}
              size="small"
            />
          )}
          onChange={handleProfileChange}
          loading={isProfilesLoading}
        />
        {isFetchingPrompts && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
            <CircularProgress
              size={12}
              sx={{ color: "rgba(144,83,246,0.7)" }}
            />
            <Typography
              sx={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}
            >
              {t("loadingSavedPrompts")}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );

  // ─── Horizontal progress stepper ─────────────────────────────────────────
  const renderStepper = () => (
    <Box sx={cls.stepperWrap}>
      <Stepper
        activeStep={Math.min(currentPhase - 1, TOTAL_PHASES)}
        alternativeLabel
        connector={<GradientConnector />}
      >
        {Array.from({ length: TOTAL_PHASES }, (_, i) => (
          <Step key={i} completed={isPhaseCompleted(i)}>
            <StepLabel StepIconComponent={CustomStepIcon}>
              {t(PHASE_AGENT_NAMES[i])}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );

  // ─── Status chip ──────────────────────────────────────────────────────────
  const renderStatusChip = (i: number) => {
    const isActive = isPhaseActive(i) && !!profile;
    const isCompleted = isPhaseCompleted(i);
    if (isCompleted)
      return (
        <Chip
          icon={<CheckRoundedIcon sx={{ fontSize: "13px !important" }} />}
          label={t("phaseStatusCompleted")}
          size="small"
          sx={cls.statusChipCompleted}
        />
      );
    if (isActive)
      return (
        <Chip
          icon={<PlayArrowRoundedIcon sx={{ fontSize: "14px !important" }} />}
          label={t("phaseStatusActive")}
          size="small"
          sx={cls.statusChipActive}
        />
      );
    return (
      <Tooltip title={t("completePreviousPhase")} arrow>
        <Chip
          icon={<LockOutlinedIcon sx={{ fontSize: "13px !important" }} />}
          label={t("phaseStatusLocked")}
          size="small"
          sx={cls.statusChipLocked}
        />
      </Tooltip>
    );
  };

  // ─── Output block ─────────────────────────────────────────────────────────
  const renderOutput = (i: number) => {
    if (!outputs[i]) return null;
    return (
      <Box sx={cls.outputWrap}>
        <Box sx={cls.outputHeader}>
          <AutoAwesomeMosaicIcon
            sx={{ fontSize: 15, color: "rgba(144,83,246,0.85)" }}
          />
          <Typography sx={cls.outputHeaderLabel}>{t("phaseOutput")}</Typography>
          <Box sx={cls.regenerateButton} onClick={() => handleRegenerate(i)}>
            <ReplayRoundedIcon
              sx={{ fontSize: 13, color: "rgba(255,185,50,0.9)" }}
            />
            <Typography sx={cls.regenerateButtonText}>
              {t("regenerate")}
            </Typography>
          </Box>
        </Box>
        <Box sx={cls.outputBody}>
          <ReactMarkdown
            remarkPlugins={[require("remark-gfm")]}
            components={markdownComponents}
          >
            {(outputs[i] || "").replace(/\\n/g, "\n")}
          </ReactMarkdown>
        </Box>
      </Box>
    );
  };

  // ─── Individual phase accordion ───────────────────────────────────────────
  const renderPhaseAccordion = (i: number) => {
    const isActive = isPhaseActive(i) && !!profile;
    const isCompleted = isPhaseCompleted(i);
    const isLocked = !isActive && !isCompleted;
    const isLoading = loadings[i];
    const expanded = getAccordionExpanded(i);

    const accordionSx = {
      ...cls.accordionBase,
      ...(isActive ? cls.accordionActive : {}),
      ...(isCompleted ? cls.accordionCompleted : {}),
      ...(isLocked ? cls.accordionLocked : {}),
    };

    const badgeSx = {
      ...cls.summaryBadgeDefault,
      ...(isActive ? cls.summaryBadgeActive : {}),
      ...(isCompleted ? cls.summaryBadgeCompleted : {}),
    };

    return (
      <Accordion
        key={i}
        expanded={expanded}
        onChange={isCompleted ? () => handleCompletedToggle(i) : undefined}
        disableGutters
        sx={accordionSx}
      >
        <AccordionSummary
          expandIcon={
            isCompleted ? (
              <ExpandMoreRoundedIcon sx={{ fontSize: 22 }} />
            ) : isActive ? null : (
              <LockOutlinedIcon sx={{ fontSize: 18, opacity: 0.4 }} />
            )
          }
          sx={cls.accordionSummary}
        >
          {/* Phase badge */}
          <Box sx={badgeSx}>
            {isCompleted ? (
              <CheckRoundedIcon sx={{ fontSize: 20 }} />
            ) : (
              <Typography
                sx={{ fontWeight: 700, fontSize: "15px", color: "inherit" }}
              >
                {i + 1}
              </Typography>
            )}
          </Box>

          {/* Title + subtitle */}
          <Box sx={cls.summaryTitleGroup}>
            <Typography sx={cls.summaryTitle}>
              {t(PHASE_AGENT_NAMES[i])}
            </Typography>
            <Typography sx={cls.summarySubtitle}>
              {t(PHASE_SUBTITLES[i])}
            </Typography>
          </Box>

          {/* Status chip */}
          {renderStatusChip(i)}
        </AccordionSummary>

        <AccordionDetails sx={cls.accordionDetails}>
          {isLocked ? (
            <Box sx={cls.lockedPlaceholder}>
              <LockOutlinedIcon
                sx={{ color: "rgba(255,255,255,0.2)", fontSize: 20 }}
              />
              <Typography sx={cls.lockedText}>
                {t("completePreviousPhase")}
              </Typography>
            </Box>
          ) : isCompleted ? (
            /* Completed: show the output only */
            renderOutput(i)
          ) : (
            /* Active: saved suggestion + prompt input + generate button + output */
            <>
              <Box sx={{ position: "relative" }}>
                <TextField
                  multiline
                  minRows={3}
                  maxRows={7}
                  placeholder={t("enterPromptForPhase", { phase: i + 1 })}
                  value={prompts[i]}
                  onChange={(e) =>
                    setPrompts((prev) => {
                      const n = [...prev];
                      n[i] = e.target.value;
                      return n;
                    })
                  }
                  disabled={isLoading}
                  sx={cls.promptTextField}
                />
                {savedPromptTexts[i] && (
                  <Box
                    sx={cls.fillTemplateButton}
                    onClick={() => handleApplySavedPrompt(i)}
                  >
                    <AutoAwesomeIcon sx={cls.fillTemplateIcon} />
                    <Typography sx={cls.fillTemplateButtonText}>
                      {t("fillTemplate")}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box sx={cls.generateRow}>
                {/* Save Prompt button */}
                <Box sx={cls.saveButtonWrap}>
                  <CustomButton
                    label={
                      savingPhase === i ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress
                            size={14}
                            sx={{ color: "rgba(255,255,255,0.6)" }}
                          />
                          {t("saving")}
                        </Box>
                      ) : savedPhases.has(i) ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <BookmarkAddedRoundedIcon sx={{ fontSize: 16 }} />
                          {t("promptSaved")}
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <BookmarkBorderRoundedIcon sx={{ fontSize: 16 }} />
                          {t("savePrompt")}
                        </Box>
                      )
                    }
                    type={strings.SECONDARY}
                    onClick={() => handleSavePrompt(i)}
                    disabled={
                      !prompts[i].trim() || savingPhase !== null || isLoading
                    }
                    customClasses={{}}
                  />
                </Box>

                {/* Generate button */}
                <Box sx={cls.generateButtonWrap}>
                  <CustomButton
                    label={
                      isLoading ? (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <CircularProgress
                            size={15}
                            sx={{ color: "rgba(255,255,255,0.7)" }}
                          />
                          {t("generating")}
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <SendRoundedIcon sx={{ fontSize: 16 }} />
                          {t("generate")}
                        </Box>
                      )
                    }
                    type={strings.PRIMARY}
                    onClick={() => handleGenerate(i)}
                    disabled={!prompts[i].trim() || isLoading || isAnyLoading}
                    customClasses={{}}
                  />
                </Box>
              </Box>

              {/* Output appears in same accordion after generation */}
              <Collapse in={!!outputs[i]} timeout={500}>
                {renderOutput(i)}
              </Collapse>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  // ─── Completion banner ────────────────────────────────────────────────────
  const renderCompletionBanner = () =>
    allDone ? (
      <Box sx={cls.completionBanner}>
        <Box sx={cls.completionIcon}>
          <EmojiEventsIcon sx={{ color: "#fff", fontSize: 30 }} />
        </Box>
        <Typography sx={cls.completionTitle}>
          {t("allPhasesComplete")}
        </Typography>
        <Typography sx={cls.completionSubtitle}>
          {t("allPhasesCompleteDesc")}
        </Typography>
        <Box sx={cls.downloadDocxButtonWrap}>
          <CustomButton
            label={
              isDownloading ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress
                    size={15}
                    sx={{ color: "rgba(255,255,255,0.7)" }}
                  />
                  {t("downloadingDocx")}
                </Box>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />
                  {t("downloadDocx")}
                </Box>
              )
            }
            type={strings.PRIMARY}
            onClick={handleDownloadDocx}
            disabled={isDownloading}
            customClasses={{}}
          />
        </Box>
      </Box>
    ) : null;

  // ─── Phase counter row ────────────────────────────────────────────────────
  const completedCount = Math.min(currentPhase - 1, TOTAL_PHASES);

  return (
    <>
      <Box sx={cls.mainContainer}>
        <Box sx={cls.mainBox}>
          <Box sx={cls.subBox}>
            <Box sx={cls.scrollStyle}>
              <Container maxWidth="xl" sx={{ px: 1 }}>
                {renderHeader()}
                {renderProfileCard()}
                {renderStepper()}

                {/* Phase count label */}
                <Box sx={cls.phaseCounterWrap}>
                  <Typography sx={cls.phaseCounterText}>
                    <Box component="span" sx={cls.phaseCounterAccent}>
                      {completedCount}
                    </Box>
                    {" / "}
                    <Box component="span" sx={cls.phaseCounterAccent}>
                      {TOTAL_PHASES}
                    </Box>{" "}
                    {t("phasesCompleted")}
                  </Typography>
                </Box>

                {/* Phase accordions */}
                {Array.from({ length: TOTAL_PHASES }, (_, i) =>
                  renderPhaseAccordion(i),
                )}

                {renderCompletionBanner()}
              </Container>
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomLoader
        isLoading={isAnyLoading || isProfilesLoading || isFetchingPrompts}
      />
    </>
  );
};

export default ProfessionPhased;
